import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode, useCallback, useEffect } from 'react';

import { SymbolScope } from '@flipster/features/src/shared/symbol/bunja';
import { TradingPauseNotice } from '@flipster/features/src/shared/trading-pause/notice';
import { tradingSessionCheckBunja } from '@flipster/features/src/shared/trading-suspension/state';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import SettingIcon from '@flipster/react/icon/generated/SettingIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { getLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';
import { OrderMarginType } from '@flipster/utils/types/base';

import AdjustLeverageModal from '@web/app/[locale]/(web)/_modal/AdjustLeverageModal';
import TradeSettingsModal from '@web/app/[locale]/(web)/_modal/TradeSettingsModal';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import LossProtectedBanner from '@web/components/LossProtected/LossProtectedBanner';
import { lossProtectionBunja } from '@web/components/LossProtected/bunja';

import Actions from './Actions';
import LiquidationInfo from './LiquidationInfo';
import MaxSlippage from './MaxSlippage';
import OnboardingTooltip from './Onboarding/Tooltip';
import OrderSizeInfo from './OrderSizeInfo';
import OrderTypeForm from './OrderTypeForm';
import OrderTypeInfo from './OrderTypeInfo';
import PriceForm from './PriceForm';
import ReduceOnly from './ReduceOnly';
import TpSl from './TpSl';
import TradingFee from './TradingFee';
import { adjustLeverageIntentBunja } from './bunja/adjust-leverage-intent';
import { useMarginType } from './bunja/base';
import { formBunja } from './bunja/form';
import MarginTypeSelectModal from './modals/margin-type-select-modal';
import { NavigationChip } from './navigation-chip';

type TabType = 'market' | 'trigger' | 'limit';
const Tabs = createTabsComponent<TabType>();

const marginTypeDisplay: Record<OrderMarginType, ReactNode> = {
  Isolated: <FormattedMessage id='Trade.r251008.Isolated' />,
  Cross: <FormattedMessage id='Trade.r251008.Cross' />,
};

// margin 주문일땐
// - Quantity: buy, sell 값 다름
// - Order size: buy, sell 값 같음
// position 주문 usdt 인풋일땐 (amount)
// - Margin: buy, sell 값 같음
// - Order size: buy, sell 값 같음
// position 주문 btc 인풋일땐 (qty)
// - Margin: buy, sell 값 다름 (bid, ask)
// - Order size: buy, sell 값 다름 (bid, ask)
const tabDescriptionKey = {
  market: 'Trade.r260506.Market_order_desc',
  limit: 'Trade.r260506.Limit_order_desc',
  trigger: 'Trade.r260506.Trigger_order_desc',
} as const;

const PerpetualOrderForm = () => {
  const t = useTranslations();
  const transTrade = useTranslations('Trade.r250401');
  const transTradeLimit = useTranslations('Trade.r251104');
  const overlay = useOverlay();

  const isMounted = useMounted();

  const store = useStore();
  const { symbol } = useBunja(tradeSymbolBunja);
  const { priceTypeAtom, resetDirty, leverageAtom, handleLeverageChangeAtom } = useBunja(formBunja);
  const handleLeverageChange = useSetAtom(handleLeverageChangeAtom);
  const [priceTypeTab, setPriceTypeTab] = useAtom<TabType>(priceTypeAtom);

  const { marginTypeAtom } = useMarginType();
  const marginType = useAtomValue(marginTypeAtom);
  // chip 라벨 표시용 leverage는 keystroke 리렌더를 타지 않는 selector로 구독한다.
  const leverage = useAtomValue(leverageAtom);
  // 레버리지 chip이 열 모달 형태(+off-hours 안내 대상 여부)는 데이터 레이어에서 결정한다.
  // intent는 클릭 시점에만 필요하므로 구독하지 않고 store.get으로 읽는다
  // (orderState/position/socket 갱신마다 폼 루트가 리렌더되는 것을 방지).
  const { adjustLeverageIntentAtom } = useBunja(adjustLeverageIntentBunja);

  const { isLossProtectedAtom } = useBunja(lossProtectionBunja);
  const isLossProtected = useAtomValue(isLossProtectedAtom);

  const { guardOffHoursAlert } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(symbol)]);

  const onClickTab = (val: TabType) => {
    segment.track(TradeEvent.TabClick, { symbol, tab_type: val });
    setPriceTypeTab(val);
    resetDirty();
  };

  const openMarginModeModal = useCallback(() => {
    overlay.open(({ isOpen, close, unmount }) => (
      <MarginTypeSelectModal isOpen={isOpen} onClose={close} onExit={unmount} />
    ));
  }, [overlay]);

  const openAdjustLeverageModal = useCallback(async () => {
    const adjustLeverageIntent = store.get(adjustLeverageIntentAtom);
    // one-way 모드면 off-hours 시 조정 전 안내 (off-hours가 아니거나 플래그 off면 guard가 즉시 통과).
    if (adjustLeverageIntent.needsOffHoursGuard && !(await guardOffHoursAlert('adjustLeverage'))) return;

    overlay.open(() =>
      adjustLeverageIntent.kind === 'position' ? (
        <AdjustLeverageModal type='position' from='orderform' position={adjustLeverageIntent.position} />
      ) : (
        <AdjustLeverageModal
          type='form'
          from='orderform'
          symbol={symbol}
          defaultLeverage={adjustLeverageIntent.defaultLeverage}
          onConfirmLeverage={handleLeverageChange}
        />
      ),
    );
  }, [store, adjustLeverageIntentAtom, overlay, symbol, handleLeverageChange, guardOffHoursAlert]);

  const openTradeSettingsModal = useCallback(() => {
    overlay.open(({ isOpen, close, unmount }) => (
      <TradeSettingsModal isOpen={isOpen} onClose={close} onSuccess={close} onExit={unmount} />
    ));
    segment.track(TradeEvent.TradeSettingsClick, {
      symbol,
      order_type: priceTypeTab,
      trade_type: 'perpetual',
      is_popup: false,
    });
  }, [overlay, symbol, priceTypeTab]);

  useEffect(() => {
    const prevPriceTypeTab = getLocalStorage('flipster.configs.v1.priceTypeTab');
    if (prevPriceTypeTab === 'trigger') {
      setPriceTypeTab('trigger');
    }
  }, [setPriceTypeTab]);

  if (!isMounted) return null;
  return (
    <>
      <div className={hstack({ justify: 'space-between', p: 16 })}>
        <div className={hstack({ gap: 8 })}>
          <NavigationChip label={marginTypeDisplay[marginType]} onClick={openMarginModeModal} />
          <NavigationChip label={`${leverage}x`} onClick={openAdjustLeverageModal} />
        </div>
        <IconButton hierarchy='quaternary' size='medium' icon={<SettingIcon />} onClick={openTradeSettingsModal} />
      </div>
      <OnboardingTooltip>
        <div>
          {isLossProtected && <LossProtectedBanner />}
          <div className={css({ pos: 'relative' })}>
            <Tabs type='scrollable' border value={priceTypeTab} onChange={onClickTab}>
              <Tabs.Tab label={transTradeLimit('Limit')} value='limit' />
              <Tabs.Tab label={transTrade('Tab_market')} value='market' />
              <Tabs.Tab label={transTrade('Tab_trigger')} value='trigger' />
            </Tabs>
            <Tooltip.Root placement='bottom-end' maxWidth={280}>
              <Tooltip.Trigger asChild>
                <IconButton
                  hierarchy='quaternary'
                  size='medium'
                  icon={<InformationCircleIcon size={20} />}
                  aria-label={t(tabDescriptionKey[priceTypeTab])}
                  css={{ pos: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)' }}
                />
              </Tooltip.Trigger>
              <Tooltip.Content portal>{t(tabDescriptionKey[priceTypeTab])}</Tooltip.Content>
            </Tooltip.Root>
          </div>
        </div>
      </OnboardingTooltip>
      <div
        className={css({
          pt: priceTypeTab === 'trigger' ? 16 : 12,
          pb: 16,
          px: 16,
        })}
      >
        <TradingPauseNotice symbol={symbol} />
        <PriceForm />
        <OrderTypeForm />
        <OrderTypeInfo />
        {priceTypeTab === 'market' && <MaxSlippage />}
        <ReduceOnly />
        <TpSl />
        <Actions />
        <OrderSizeInfo />
        <LiquidationInfo />
        <TradingFee />
      </div>
    </>
  );
};

export default PerpetualOrderForm;
